// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { memo, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import BookingCalendar from 'presentations/views/components/organisms/BookingCalendar';
import { SharedGuestUserItem, ViewerItem } from 'presentations/api/schema/user';
import { colorPalette, white } from 'presentations/views/styles/color';
import { mediaMax } from 'presentations/views/styles';
import { CalendarProvider } from 'presentations/views/components/atoms/Calendar/reducer/CalendarContext';
import {
  CalendarMode,
  CalendarSource,
  Errors,
  EventListItem,
  Schedule,
  ScheduleType,
  ShowRange,
  SlotViewType,
  CalendarWeekDay,
} from '../../atoms/Calendar/reducer';
import {
  DateTime,
  currentTimeZone,
  formatToTimeShort,
  getDateTime,
} from 'infras/datetime';
import {
  TimeListViewerCalendarEvent,
  ViewerCalendarEventListItem,
} from 'presentations/api/schema/calendar_event';
import { sprintf } from 'sprintf-js';
import { TeamUserListItem } from 'presentations/api/schema/team_user';
import { Status } from 'domains/models/event/status';
import { ResponseStatus } from 'domains/models/event/responseStatus';
import { ProviderType } from 'domains/models/calendar/providerType';
import { Behavior } from 'domains/models/event/behavior';

interface Props {
  viewer?: ViewerItem;
  isLoading?: boolean;
  timeZone: string;
  duration: number;
  interval: number;
  startWeekday: number;
  color: string;
  isHostDiagnosticMode?: boolean;
  onSelect?: any;
  onClickBlockTime?: Function;

  viewerBlocks: ViewerCalendarEventListItem[];
  timeLists: Array<Array<(any | null)[]>>;
  allTimeLists: Array<Array<(any | null)[]>>;
  selectedDatetime: DateTime | null;
  selectedStartDate: DateTime;
  isRevealGuestUser: boolean;
  isShowViewerCalendar: boolean;
  viewerTeamUser?: TeamUserListItem;
  onClickCalendarUnavailableButton?: any;
  onClickEvent?: (e: HTMLElement, event: TimeListViewerCalendarEvent) => void;
  displayWeekdays?: CalendarWeekDay[];
}

export const minDuration = 15;
export const minInterval = 15;

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [isInit, setIsInit] = useState<boolean>(false);

  useEffect(() => {
    if (!isInit && props.isLoading === false) {
      setIsInit(true);
    }
  }, [props.isLoading]);

  const viewerSchedules = useMemo(() => {
    const viewerTimeZone = currentTimeZone();
    const schedules: Schedule[] = [];
    if (!props.isShowViewerCalendar) {
      return schedules;
    }
    const calendarOffset =
      getDateTime(new Date(), props.timeZone).offset -
      getDateTime(new Date(), viewerTimeZone).offset;
    for (const event of props.viewerBlocks) {
      const startedAt = getDateTime(event.startedAt, props.timeZone).plus({
        minutes: calendarOffset,
      });
      const endedAt = getDateTime(event.endedAt, props.timeZone).plus({
        minutes: calendarOffset,
      });
      schedules.push({
        scheduleType: ScheduleType.Event,
        uid: event.uid!,
        userId: event.userId!,
        calendarId: event.calendarId!,
        eventId: event.uid!,
        url: null,
        status: Status.Open,
        responseStatus: ResponseStatus.None,
        timeZone: props.timeZone,
        description: null,
        providerType: ProviderType.Jicoo,
        calendarSource: CalendarSource.Jicoo,
        behavior: Behavior.Event,
        address: null,
        isRecurringEvent: false,
        name: event.name || t('No Event Name'),
        startedAt: startedAt.toISO(),
        endedAt: endedAt.toISO(),
        isAllDay: event.isAllDay,
        color:
          event.color ||
          props.viewerTeamUser?.color ||
          colorPalette.primary.www,
      });
    }
    return schedules;
  }, [props.viewerBlocks, props.isShowViewerCalendar]);

  const slotSchedules = useMemo(() => {
    const schedules: Schedule[] = [];
    for (let index = 0; index < props.timeLists.length; index++) {
      const timeList = props.timeLists[index];
      const date = props.selectedStartDate.plus({
        days: index,
      });

      for (const time of timeList) {
        // const viewerEventName = time[2] || null;
        // const isTransparentEvent = time[6] === Transparency.Transparent;
        // if (viewerEventName && !isTransparentEvent) {
        //   continue;
        // }

        const timeString = `${sprintf('%02d', time[0])}:${sprintf(
          '%02d',
          time[1],
        )}`;
        const startedAt = date.set({
          hour: typeof time[0] === 'number' ? time[0] : undefined,
          minute: typeof time[1] === 'number' ? time[1] : undefined,
          second: 0,
          millisecond: 0,
        });
        const endedAt = startedAt.plus({
          minute: props.duration,
        });

        if (time[3] === 0) {
          continue;
        }
        const remainingCapacity = time[3] ? Number(time[3]) : null;
        let capacityText: string | null = null;
        let isFullCapacity = false;
        if (time.length >= 4 && remainingCapacity !== null) {
          if (remainingCapacity > 0) {
            capacityText = t('remaining capacity', {
              count: time[3],
            });
          } else {
            isFullCapacity = true;
            capacityText = t('full capacity');
          }
        }

        const guestUsers: SharedGuestUserItem[] | null =
          props.isRevealGuestUser && time && time.length >= 5 ? time[4] : null;

        schedules.push({
          scheduleType: ScheduleType.Availability,
          uid: `${timeString}`,
          userId: '',
          calendarId: '',
          eventId: '',
          url: null,
          status: Status.Open,
          responseStatus: ResponseStatus.None,
          timeZone: props.timeZone,
          description: null,
          providerType: ProviderType.Jicoo,
          calendarSource: CalendarSource.Jicoo,
          behavior: Behavior.Event,
          address: null,
          isRecurringEvent: false,
          name: timeString,
          startedAt: startedAt.toISO(),
          endedAt: endedAt.toISO(),
          isAllDay: false,
          color: '',
          customData: {
            capacity: {
              label: capacityText,
              isFull: isFullCapacity,
              remaining: remainingCapacity,
            },
            guestUsers,
          },
        });
      }
    }
    return schedules;
  }, [
    props.timeLists,
    props.selectedStartDate,
    props.selectedDatetime,
    props.timeZone,
    props.duration,
  ]);

  const hiddenWeekdays = useMemo(() => {
    const defaultWeekdays: CalendarWeekDay[] = [0, 1, 2, 3, 4, 5, 6];
    if (!props.displayWeekdays) return defaultWeekdays;

    const weekdays: CalendarWeekDay[] = [];
    for (const weekday of defaultWeekdays) {
      if (props.displayWeekdays.includes(weekday)) {
        continue;
      }
      weekdays.push(weekday);
    }
    return weekdays;
  }, [props.displayWeekdays]);

  const { minHour, maxHour } = useMemo(() => {
    let minHour: number | null = null;
    let maxHour: number | null = null;
    for (const timeList of props.allTimeLists) {
      for (const times of timeList) {
        const startHour = times[0];
        const endHour = Math.ceil(startHour + props.duration / 60);
        if (!minHour || startHour < minHour) {
          minHour = startHour;
        }
        if (!maxHour || endHour > maxHour) {
          maxHour = endHour;
        }
      }
    }
    if (!minHour) {
      minHour = 0;
    } else {
      minHour--;
      minHour = minHour < 0 ? 0 : minHour;
    }
    if (!maxHour) {
      maxHour = 24;
    } else {
      maxHour++;
      maxHour = maxHour > 24 ? 24 : maxHour;
    }
    return { minHour, maxHour };
  }, [props.allTimeLists]);

  const showRange: ShowRange = { day: 7 };
  const onClickCalendarEvent = (event: any, element: HTMLElement) => {
    if (!props.onClickEvent) return;
    const calendarEvent: TimeListViewerCalendarEvent = {
      name: event.name,
      color: event.color,
      uid: event.eventId,
      userId: event.userId,
      accountId: event.accountId,
      calendarId: event.calendarId,
    };
    props.onClickEvent(element, calendarEvent);
  };

  const onClickSlot = (event: EventListItem) => {
    const guestUsers = event.customData?.guestUsers || null;
    props.onSelect(
      formatToTimeShort(event.startedAt),
      event.startedAt,
      guestUsers,
    );
  };

  const onCalendarError = (errors: Errors[]) => {
    for (const error of errors) {
      switch (error.code) {
        default:
          break;
      }
    }
  };

  const isInvalidSetting = false;
  // const isInvalidSetting =
  //   props.duration < minDuration || props.interval < minInterval;

  return (
    <CalendarProvider
      duration={props.duration}
      rulerStep={props.interval}
      calendarMode={CalendarMode.Booking}
      showRange={showRange}
      slotViewType={SlotViewType.SlotAndGroup}
      startWeekday={props.startWeekday as CalendarWeekDay}
      currentDate={props.selectedStartDate.toISO()}
      hiddenWeekdays={hiddenWeekdays}
      isDisableMultiTimeZones={true}
      timeZone={props.timeZone}
      isAutoScrollOnInit={false}
      onError={onCalendarError}
      primaryColor={props.color}
      rulerMinHour={minHour}
      rulerMaxHour={maxHour}
      isDisableHorizontalScroll
    >
      <Main ref={scrollerRef}>
        <BookingCalendar
          schedules={
            isInvalidSetting ? [] : [...viewerSchedules, ...slotSchedules]
          }
          isLoading={!isInit}
          isInvalid={isInvalidSetting}
          color={props.color}
          onClickEvent={onClickCalendarEvent}
          onClickBookingSlot={onClickSlot}
        />
      </Main>
    </CalendarProvider>
  );
});

const Main = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${white};
  overflow-y: auto;
  width: inherit;
  height: inherit;
  ${mediaMax.tablet} {
    height: inherit;
  }
  ${mediaMax.tablet} {
    max-width: 100%;
  }
`;

export default Component;
